import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef
} from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { AppSelectOption } from '../../../../../../../_base-shared/contracts/common.interface';
import { Product } from '../../../../../../../_base-shared/models/Product';
import { PaymentPlanType } from '../../../../../../../_base-shared/models/Product/PaymentPlanType';
import { ProductService } from '../../../case/product.service';
import { PaymentPlanTypeService } from '../../../payment/payment-plan-type.service';
import { CaseCreditorStatusService } from '../status-list/case-creditor-status.service';

@Component({
  standalone: false,
  selector:    'app-case-creditor-quick-status-editor',
  templateUrl: './case-creditor-quick-status-editor.component.html'
})

export class CaseCreditorQuickStatusEditorComponent implements OnInit {
  public form: UntypedFormGroup;
  public isEdit                                     = true;
  public createFees                                 = new FormControl(false);
  public paymentPlanTypes: Array<PaymentPlanType>;
  public originalPaymentPlanTypes: Array<PaymentPlanType>;
  public isLoading                                  = 0;
  public showPaymentPlanTypes                       = false;
  public showFixedAmount                            = false;
  public installmentOptions: Array<AppSelectOption> = [];
  public amountTypeOptions: Array<AppSelectOption>  = [];
  public products: Array<Product>                   = [];

  public allowedUsers = [36, 751600, 752733, 768371];

  constructor(
    public dialogRef: MatDialogRef<CaseCreditorQuickStatusEditorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private caseCreditorStatusService: CaseCreditorStatusService,
    private paymentPlanTypeService: PaymentPlanTypeService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private productService: ProductService,
  ) {
  }

  ngOnInit(): void {
    this.fetchPaymentPlanTypes();
    this.buildInstallmentOptions();
    this.buildForm();
    this.fetchProducts();
    if (!this.data) {
      this.isEdit = false;
    }
    if (this.isEdit) {
      this.form.get('slug').disable();

      if (this.data?.is_system_status || this.allowedUsers.includes(this.data?.authUser?.id)) {
        this.form.get('is_system_status').disable();
      }
    }

    this.createFees.valueChanges.subscribe(createFeeValue => {
      this.showPaymentPlanTypes = !!createFeeValue;
    });
    this.form.get('name_en').valueChanges.subscribe((value) => {
      this.form.get('slug').setValue(this.onKeyDown(value));
    });
  }

  private buildForm(): void {
    if (this.data?.creditor?.payment_plan_type_id || this.data?.creditor?.is_installment_fee) {
      this.showPaymentPlanTypes = true;
      this.createFees.setValue(true);
    }
    this.form            = new FormGroup({
      name_en:                     new UntypedFormControl(this.data?.creditor?.name_en || ''),
      name_es:                     new UntypedFormControl(this.data?.creditor?.name_es || ''),
      is_for_bulk_dm:              new UntypedFormControl(this.data?.creditor?.is_for_bulk_dm ? true : false),
      is_system_status:            new UntypedFormControl(this.data?.creditor?.is_system_status ? true : false),
      zero_active_debt:            new UntypedFormControl(this.data?.creditor?.zero_active_debt ? true : false),
      exclude_from_distribution:   new UntypedFormControl(this.data?.creditor?.exclude_from_distribution ? true : false),
      slug:                        new UntypedFormControl(this.data?.creditor?.slug || ''),
      payment_plan_type_id:        new UntypedFormControl(this.data?.creditor?.payment_plan_type_id || ''),
      is_installment_fee:          new UntypedFormControl(this.data?.creditor?.is_installment_fee ? this.data?.creditor?.is_installment_fee : true),
      amount_type:                 new UntypedFormControl(this.data?.creditor?.amount_type ? this.data?.creditor?.amount_type : null),
      fixed_amount:                new UntypedFormControl(this.data?.creditor?.fixed_amount || ''),
      flag_case_creditor_active:   new UntypedFormControl(this.data?.creditor?.flag_case_creditor_active || false),
      flag_case_creditor_complete: new UntypedFormControl(this.data?.creditor?.flag_case_creditor_complete || false),
      set_product_id:              new UntypedFormControl(this.data?.creditor?.set_product_id || []),
    });
    this.showFixedAmount = this.form?.get('amount_type').value === 'fixed';
    this.form?.get('amount_type').valueChanges.subscribe((value) => {
      this.showFixedAmount = value === 'fixed';
    });
    this.form?.get('payment_plan_type_id').valueChanges.subscribe((value) => {
      this.buildAmountTypeOptions();
    });
  }

  onSubmit(): void {
    const data = this.form.getRawValue();
    if (this.data?.creditor?.id) {
      this.caseCreditorStatusService.update(this.data.creditor?.id, data).subscribe(() => {
        this.toastr.success(this.translate.instant('SHARED.success'));
        this.dialogRef.close();
      });
    } else {
      this.caseCreditorStatusService.store(data).subscribe(() => {
        this.dialogRef.close();
        this.toastr.success(this.translate.instant('SHARED.success'));
      });
    }
  }

  onKeyDown(text): void {
    if (this.isEdit && this.data?.creditor?.slug) {
      return this.data.creditor?.slug;
    }
    const str = text.replace(/\s+/g, '_');
    return str.toLowerCase();
  }

  private fetchPaymentPlanTypes(): void {
    this.isLoading++;
    this.paymentPlanTypeService.index().pipe(finalize(() => this.isLoading--))
      .subscribe(result => {
        this.originalPaymentPlanTypes = result.data;
        const isInstallmentFee        = this.form.get('is_installment_fee').value;
        this.paymentPlanTypes         = isInstallmentFee
          ? this.originalPaymentPlanTypes.filter(plan => plan.is_installment_fee === true)
          : [...this.originalPaymentPlanTypes];
        this.buildAmountTypeOptions();
      });
  }

  private buildInstallmentOptions(): void {
    this.installmentOptions = [
      {value: false, label: this.translate.instant('Installment')},
      {value: true, label: this.translate.instant('Fee')},
    ];
  }

  private buildAmountTypeOptions(): void {
    this.amountTypeOptions        = [
      {value: 'fixed', label: this.translate.instant('Fixed')},
    ];
    const paymentPlanTypeId       = this.form.get('payment_plan_type_id')?.value;
    const selectedPaymentPlanType = this.paymentPlanTypes.find(
      (type) => type.id === paymentPlanTypeId
    );
    if (selectedPaymentPlanType?.slug === 'management_fee' || selectedPaymentPlanType?.slug === 'negotiation_fee') {
      this.amountTypeOptions.push({value: 'calculate', label: this.translate.instant('Calculate')});
    }
  }

  public installmentOptionChanged($event): void {
    if ($event === true) {
      this.paymentPlanTypes = this.originalPaymentPlanTypes.filter(
        (plan) => plan.is_installment_fee === true
      );
    } else {
      this.paymentPlanTypes = [...this.originalPaymentPlanTypes];
    }
  }

  private fetchProducts(): void {
    this.isLoading++;
    this.productService.index({select_all: 1}).pipe(finalize(() => this.isLoading--)).subscribe({
      next:  result => this.products = result.data,
      error: err => console.error(err),
    });
  }
}
